import React, { useEffect, useState } from 'react';
import { CardSection } from '../../shared/components/card/CardSection';
import { IconButton, InputBase, Paper, Box, Stack, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { TableDefault } from '../../shared/components/table/TableDefault';
import { TableColumnType } from '../../shared/components/table/table-types';
import moment from 'moment';
import { DateRangePickerWithText } from '../../shared/components/DateRangePicker';
import { formatterDateFilter } from '../../shared/utils';
import { DateFilter } from '../../shared/constants';
import { useBreakPoint } from '../../shared/hooks/useBreakPoint';
import { useNavigate } from 'react-router-dom';
import {
  useUsersLazyQuery,
  User,
  SortOrder,
  useToggleAdministratorUserRoleMutation,
  UserRoles,
  UserOrderInput,
  SubscriptionStatuses,
} from '../../shared/types/generated';
import { openSnackbarAction } from '../../shared/components/snackbar/snackbar-actions';
import { TypeMessage } from '../../shared/components/snackbar/snackbar-types';
import { useCallAction } from '@cobuildlab/react-simple-state';
import { cancelSubscriptionToAdmin } from '../user/user-actions';
import { DialogDefault } from '../../shared/components/dialog/DialogDefault';
import { InputDefault } from '../../shared/components/inputs/InputDefault';

export const SubscriptionUsers: React.FC = () => {
  const navigate = useNavigate();
  const [total, setTotal] = useState<number>(0);
  const { isBelow } = useBreakPoint('sm');
  const [{ from, to }, setDate] = useState<{
    from: string | undefined;
    to: string | undefined;
  }>({
    from: undefined,
    to: undefined,
  });
  const [{ page, pageSize }, setPagination] = useState({
    pageSize: 10,
    page: 1,
  });

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<UserOrderInput>({
    created_at: SortOrder.Desc,
  });
  const [userToDeleteSubs, setUserToDeleteSubs] = useState<User | null>(null);
  const [openDialogDeleteSubs, setOpenDialogDeleteSubs] = useState(false);
  const [reasonToDeleteSubs, setReasonToDeleteSubs] = useState('');

  const [callUserSubscribers, { data, loading, refetch }] = useUsersLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: (d) => {
      console.log(d);
      setTotal(d?.users?.count || 0);
    },
    onError: (error) => {
      console.error(error);
      openSnackbarAction(
        'Ha ocurrido un error al intentar obtener los usuarios suscritos',
        TypeMessage.ERROR,
      );
    },
  });

  const [toogleAdministratorUserRole] = useToggleAdministratorUserRoleMutation({
    onCompleted: () => {
      refetch();
      openSnackbarAction(
        'El rol de administrador ha sido actualizado correctamente',
        TypeMessage.SUCCESS,
      );
    },
    onError: () => {
      openSnackbarAction(
        'Ha ocurrido un error al intentar actualizar el rol de administrador',
        TypeMessage.ERROR,
      );
    },
  });
  const [callCancelSubscription, loadingCancelSubs] = useCallAction(cancelSubscriptionToAdmin, {
    onCompleted() {
      refetch();
      setOpenDialogDeleteSubs(false);
      setReasonToDeleteSubs('');
      setUserToDeleteSubs(null);
      openSnackbarAction('La renovación se ha cancelado correctamente', TypeMessage.SUCCESS);
    },
    onError(error) {
      openSnackbarAction(error.message, TypeMessage.ERROR);
    },
  });

  const handleUserDetailsClick = (item: User): void => {
    navigate(`/users/${item.id}/subscriptions`);
  };

  useEffect(() => {
    callUserSubscribers({
      variables: {
        where: {
          onlySubscribers: true,
          created_at: {
            lte: to,
            gte: from,
          },
          ...(search !== '' && {
            created_at: undefined,
            OR: [
              {
                name: {
                  startsWith: search,
                },
              },
              {
                email: {
                  startsWith: search,
                },
              },
            ],
          }),
        },
        skip: (page - 1) * pageSize,
        take: pageSize,
        orderBy: sort,
      },
    });
  }, [page, pageSize, callUserSubscribers, from, to, search, sort]);

  const COLUMNS: TableColumnType<User>[] = [
    {
      columnName: 'NOMBRE',
      columnValue: (item) => item.name || '',
    },
    {
      columnName: 'EMAIL',
      columnValue: null,
      columnComponent: (item) => <Box>{item.email || ''}</Box>,
    },
    {
      columnName: 'FECHA DE SUSCRIPCIÓN',
      columnValue: (item) =>
        item?.subscriptions?.[0]?.created_at
          ? moment(parseInt(item?.subscriptions?.[0]?.created_at))
              .format('YYYY-MM-DD HH:mm')
          : '',
    },
    {
      columnName: 'PLAN',
      columnValue: (item) => item?.subscriptions?.[0]?.plan?.title || '',
    },
    {
      columnName: 'N° DE SUSCRIPCIÓN',
      columnValue: (item) => item?.subscriptions?.[0]?.transaction_id || '',
    },
    {
      columnName: 'METODO DE PAGO',
      columnValue: (item) => item?.subscriptions?.[0]?.payment_method || '',
    },
  ];

  const users = (data?.users?.items || []) as User[];

  return (
    <Box sx={{ width: '100%', background: 'white' }}>
      <DialogDefault
        title="Cancelar renovación"
        isOpen={openDialogDeleteSubs}
        handleClose={() => {
          setUserToDeleteSubs(null);
          setOpenDialogDeleteSubs(false);
        }}
        isLoading={loadingCancelSubs}
        disabledAction={!reasonToDeleteSubs || reasonToDeleteSubs.trim() === ''}
        handleSuccess={() => {
          if (userToDeleteSubs?.subscriptions && userToDeleteSubs?.subscriptions?.length > 0) {
            const subscription = userToDeleteSubs?.subscriptions?.find(
              (s) => s?.status === SubscriptionStatuses.Active,
            );
            if (subscription) {
              callCancelSubscription({
                reason: reasonToDeleteSubs,
                subscriptionId: subscription.id as number,
              });
            }
          }
        }}
        showActions
      >
        <Typography>
          ¿Estás seguro de cancelar la renovacion de la suscripción de <strong>{userToDeleteSubs?.name}</strong>?
        </Typography>
        <InputDefault
          value={reasonToDeleteSubs}
          placeholder="Por favor escriba el motivo"
          onChange={(e) => {
            setReasonToDeleteSubs(e.target.value);
          }}
          variant="outlined"
          sx={{ mt: 2.5 }}
          multiline
          minRows={3}
          maxRows={5}
        />
      </DialogDefault>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        width={'100%'}
        alignItems={'center'}
        justifyContent={{ xs: 'space-between' }}
        py={1}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box pl={2} display={'flex'} alignItems={'center'}>
          <Typography fontSize={{ sx: '16px', md: '20px' }} fontWeight={600}>
            Usuarios suscritos
          </Typography>
        </Box>

        <Box sx={{ width: { sx: '100%', md: 'auto' } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            padding={'10px 0px'}
            spacing={1}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            <Paper
              sx={{
                p: '2px 4px',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                maxWidth: 400,
                height: '30px',
              }}
            >
              <InputBase
                sx={{ ml: 1, flex: 1 }}
                placeholder={isBelow ? 'BUSCAR' : 'BUSCAR USUARIO'}
                inputProps={{ 'aria-label': 'search google maps' }}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                size="small"
                onKeyUp={(e) => {
                  if (e.code === 'Enter') {
                    setPagination({
                      pageSize,
                      page: 1,
                    });
                  }
                }}
              />

              <IconButton
                type="button"
                sx={{ p: '10px' }}
                aria-label="search"
                onClick={() => {
                  setPagination({
                    pageSize,
                    page: 1,
                  });
                }}
              >
                <SearchIcon />
              </IconButton>
            </Paper>
            <Box display="flex" alignItems="center">
              <DateRangePickerWithText
                onChange={(range) => {
                  const filters = formatterDateFilter(
                    range.startDate,
                    range.endDate,
                    range.key as DateFilter,
                  );

                  setDate({
                    from: filters.startDate,
                    to: filters.endDate,
                  });
                }}
              />
            </Box>
          </Stack>
        </Box>
      </Stack>
      <CardSection title={''}>
        <TableDefault
          items={users}
          count={total}
          columns={COLUMNS}
          page={page}
          pageSize={pageSize}
          loading={loading}
          messageLoading={'Cargando usuarios...'}
          messageEmpty="No se encontraron usuarios"
          handleClickRow={(item) => handleUserDetailsClick(item)}
          handleSort={(field, order) => {
            setSort({
              [field]: order,
            });
          }}
          handleChangePage={(p) => {
            setPagination({
              pageSize,
              page: p + 1,
            });
          }}
          showPagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          handleChangeRowPerPage={(e) => {
            setPagination({
              pageSize: parseInt(e.target.value, 10),
              page: 1,
            });
          }}
          actions={[
            {
              actionName: 'Ver detalles',
              onClick: (item) => handleUserDetailsClick(item),
            },
            {
              actionName: 'Cancelar renovación',
              onClick: (item) => {
                setUserToDeleteSubs(item);
                setOpenDialogDeleteSubs(true);
              },
              isDisabled: (item) =>
                !item?.subscriptions?.some((s) => s?.status === SubscriptionStatuses.Active),
            },
            {
              actionName: (item) => {
                if (item.roles_on_users?.some((r) => r?.role?.name === UserRoles.Administrator)) {
                  return 'Quitar rol de administrador';
                } else {
                  return 'Asignar rol de administrador';
                }
              },
              onClick: (item) => {
                toogleAdministratorUserRole({
                  variables: {
                    data: {
                      user_id: item.id,
                    },
                  },
                });
                setPagination({
                  pageSize,
                  page: 1,
                });
              },
            },
          ]}
        />
      </CardSection>
    </Box>
  );
};
